import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import gettext from '@eb/gettext';

import { Icon } from '@eb/eds-icon';
import { ArrowRightChunky } from '@eb/eds-iconography';
import { TicketChunky } from '@eb/eds-iconography';
import { CircleFill } from '@eb/eds-iconography';
import { isMediumNarrowDown } from '@eb/eds-utils';

import Countdown from './Countdown';

import NotificationHeader from './NotificationHeader';
import { LiveEventContext } from '../context/Event';

import './AttendeeTicketNotificationHeader.scss';

const LiveEventHeaderContent = () => (
    <span className="eds-text-color--primary-brand--hover">
        {gettext('This event is live')}
    </span>
);

const UpcomingEventHeaderContent = ({ eventStart, hasDigitalContent }) => {
    //Translators: Numerical countdown occurs after the text
    //Translators: ex. Going live in 3:23:12
    const digitalContentText = gettext('Going live in');
    const defaultText = gettext('Event starts in');

    return (
        <span className="eds-text-color--grey-800">
            {hasDigitalContent ? digitalContentText : defaultText}{' '}
            <Countdown eventStart={eventStart} />
        </span>
    );
};

const HEADER_CONTENT_MAP = {
    live_online: {
        iconContainerClass:
            'notification-header__icon--small notification-header__icon--online-live',
        iconProps: {
            type: <CircleFill />,
            size: 'small',
            color: 'ui-orange',
        },
        HeaderContent: LiveEventHeaderContent,
    },
    live_physical: {
        iconContainerClass: 'notification-header__icon--medium',
        iconProps: {
            type: <TicketChunky />,
            size: 'medium',
            color: 'ui-orange',
        },
        HeaderContent: LiveEventHeaderContent,
    },
    upcoming: {
        iconContainerClass: 'notification-header__icon--medium',
        iconProps: {
            type: <TicketChunky />,
            color: 'grey-800',
            size: 'medium',
        },
        HeaderContent: UpcomingEventHeaderContent,
    },
};

const AttendeeTicketNotificationHeader = (props) => {
    const isEventLive = useContext(LiveEventContext);
    const headerContentKey = isEventLive
        ? props.hasDigitalContent
            ? 'live_online'
            : 'live_physical'
        : 'upcoming';

    const { iconContainerClass, iconProps, HeaderContent } = HEADER_CONTENT_MAP[
        headerContentKey
    ];

    return (
        <NotificationHeader
            containerClasses={
                isMediumNarrowDown() ? 'eds-text-bl' : 'eds-text-hs'
            }
            renderIcon={(baseIconClass) => {
                return (
                    <span className={`${baseIconClass} ${iconContainerClass}`}>
                        <Icon {...iconProps} />
                    </span>
                );
            }}
        >
            <HeaderContent {...props} />
        </NotificationHeader>
    );
};

const AttendeeTicketNotificationContent = (props) => (
    <>
        <AttendeeTicketNotificationHeader {...props} />
        <div className="eds-text-bs eds-l-pad-top-2 eds-text-color--grey-700">
            {props.eventName}
        </div>
        <div className="eds-l-pad-top-4">
            <a
                className="eds-text-weight--heavy eds-text-bs"
                href={props.ctaLinkUrl}
                onClick={props.onLinkClick}
                target="_blank"
                rel="noopener noreferrer"
            >
                {props.hasDigitalContent
                    ? gettext('Join now')
                    : gettext('View tickets')}{' '}
                <Icon
                    type={<ArrowRightChunky />}
                    size="xsmall"
                    color="ui-blue"
                />
            </a>
        </div>
    </>
);

AttendeeTicketNotificationContent.propTypes = {
    eventName: PropTypes.string,
    ctaLinkUrl: PropTypes.string,
    onLinkClick: PropTypes.func,
    hasDigitalContent: PropTypes.bool,
    eventStart: PropTypes.string,
};

export default AttendeeTicketNotificationContent;
